import React, { useState, useEffect } from "react";
import Header from "./Header";

const UserProfile = () => {
  const [rechargeStatus, setRechargeStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const ClientName = localStorage.getItem("username");
  const ClientRole = localStorage.getItem("role");
  const ClientDevices = localStorage.getItem("device_ids");
  
  // Parse the device IDs from string
  let deviceArray = [];
  
  try {
    // If it's a JSON string (like ["device1", "device2"])
    const parsedDevices = JSON.parse(ClientDevices);
    
    if (Array.isArray(parsedDevices)) {
      deviceArray = parsedDevices;
    }
  } catch (e) {
    // If it's not valid JSON, try comma-separated format
    if (ClientDevices && ClientDevices.includes(',')) {
      deviceArray = ClientDevices.split(',').map(device => device.trim());
    } else if (ClientDevices) {
      deviceArray = [ClientDevices];
    }
  }

  // Function to fetch recharge status for a device
  const fetchRechargeStatus = async (deviceId) => {
    try {
      const token = localStorage.getItem("token"); // Assuming you store JWT token
      const response = await fetch(`/api/recharge-status/${deviceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error(`Failed to fetch recharge status for ${deviceId}`);
        return { is_valid: false, plan_info: { error: "Failed to fetch" } };
      }
    } catch (error) {
      console.error(`Error fetching recharge status for ${deviceId}:`, error);
      return { is_valid: false, plan_info: { error: "Network error" } };
    }
  };

  // Fetch recharge status for all devices
  useEffect(() => {
    const fetchAllRechargeStatus = async () => {
      setLoading(true);
      const statusMap = {};

      for (const deviceId of deviceArray) {
        const status = await fetchRechargeStatus(deviceId);
        statusMap[deviceId] = status;
      }

      setRechargeStatus(statusMap);
      setLoading(false);
    };

    if (deviceArray.length > 0) {
      fetchAllRechargeStatus();
    } else {
      setLoading(false);
    }
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  // Get status color based on validity
  const getStatusColor = (isValid) => {
    return isValid ? "text-green-400" : "text-red-400";
  };

  // Get status text
  const getStatusText = (isValid, endDate) => {
    if (isValid) {
      return `Valid until: ${formatDate(endDate)}`;
    } else {
      return "Expired/Invalid";
    }
  };

  return (
    <>
      <Header/>
      <div className="flex justify-center items-center h-full py-40">
        <div className="bg-primary shadow-lg rounded-lg p-6 w-full max-w-2xl">
          <div className="flex flex-col items-center mb-6">
            <img
              src="/images/client.webp"
              alt="Profile"
              className="w-24 h-24 rounded-full mb-4 shadow-md"
            />
            <h2 className="text-2xl font-semibold text-white">{ClientName}</h2>
            <p className="text-gray-500 text-2xl bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
              {ClientRole}
            </p>
          </div>

          <div className="mt-6">
            <h3 className="text-2xl font-bold text-white mb-4">Devices & Recharge Status</h3>
            
            {loading ? (
              <div className="text-white text-center">
                <p>Loading recharge status...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {deviceArray.map((deviceId, index) => {
                  const status = rechargeStatus[deviceId];
                  const isValid = status?.is_valid || false;
                  const planInfo = status?.plan_info || {};

                  return (
                    <div key={index} className="bg-gray-800 rounded-lg p-4 flex justify-between items-center">
                      <div className="text-white">
                        <p className="text-lg font-semibold">{deviceId}</p>
                        <p className="text-sm text-gray-400">Device ID</p>
                      </div>

                      <div className="text-right">
                        <p className={`text-lg font-semibold ${getStatusColor(isValid)}`}>
                          {isValid ? "✅ Active" : "❌ Inactive"}
                        </p>
                        <p className="text-sm text-gray-300">
                          {getStatusText(isValid, planInfo.end_date)}
                        </p>
                        {planInfo.start_date && (
                          <p className="text-xs text-gray-400">
                            Started: {formatDate(planInfo.start_date)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {deviceArray.length === 0 && (
              <div className="text-white text-center">
                <p>No devices found</p>
              </div>
            )}
          </div>

          {/* Summary section */}
          {!loading && deviceArray.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-600">
              <div className="flex justify-between text-white">
                <div>
                  <p className="text-sm text-gray-400">Total Devices</p>
                  <p className="text-xl font-bold">{deviceArray.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Active</p>
                  <p className="text-xl font-bold text-green-400">
                    {Object.values(rechargeStatus).filter(status => status?.is_valid).length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Inactive</p>
                  <p className="text-xl font-bold text-red-400">
                    {Object.values(rechargeStatus).filter(status => !status?.is_valid).length}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserProfile;
